/**
 * The audit log: who changed what, and when (#89, register row SEC-05).
 *
 * Only the changes somebody will later dispute are recorded — a stock level
 * moved, a count accepted as the truth, a venue's price or an ingredient's
 * cost, a spec saved, an order or a wastage entry signed off, a night's till
 * closed, the approval limits moved, and anybody's access to any of that
 * changed on screen 12. Each is one immutable entry,
 * written by the route that made the change rather than by a middleware
 * guessing at intent from a URL: only the route knows what the row looked
 * like before it wrote over it.
 *
 * An entry is deliberately compact. It names the actor twice — by id, which
 * is what the rest of the table keys on, and by the name they had at the
 * time, because a person removed from the organisation next year should not
 * turn their old entries into a column of opaque ids — and carries only the
 * fields that changed, before and after. Spec saves are the exception that
 * proves the rule: every save already writes a whole `RECIPEVER#` snapshot, so
 * the entry says which version it made and which fields moved, and leaves the
 * values to the snapshot rather than keeping a second copy of the recipe.
 *
 * Entries live in their own partition beside the org's (see `auditKeys`),
 * for the same reason the chat and the till ledger do: the org and venue
 * partitions are read whole on every page load, and a log that only grows
 * does not belong in either.
 *
 * Nothing here expires. The `USAGE#` counters carry a 13-month TTL because
 * they are a budget; this is a record of who signed off a cash-up and who
 * moved a stock level, which is what an accountant or the tax office asks
 * about years later — §6 already keeps the slips behind those figures for
 * five. At a few hundred bytes an entry and a few dozen entries a night, a
 * venue's decade of history is tens of megabytes: not a cost worth a TTL.
 */
/** The groups the Activity view filters by. */
export const AUDIT_KINDS = [
    "stock",
    "price",
    "spec",
    "order",
    "wastage",
    "cashup",
    "people",
    "settings",
];
/**
 * Every event the log records, and the group each belongs to.
 *
 * Finer than the `Action` vocabulary on purpose. `stock:adjust` covers both a
 * delivery booked in and a whole count accepted, and `org:manage` covers an
 * invitation and a removal; the permission says who was allowed, the event
 * says what they did. Both are kept on the entry.
 */
export const AUDIT_EVENTS = {
    "stock.adjust": "stock",
    "count.finalise": "stock",
    "batch.make": "stock",
    "price.override": "price",
    "ingredient.cost": "price",
    "spec.create": "spec",
    "spec.save": "spec",
    "item.save": "spec",
    "recipe.promote": "spec",
    "order.approve": "order",
    "order.reject": "order",
    "order.cancel": "order",
    "wastage.approve": "wastage",
    "wastage.delete": "wastage",
    "cashup.finalise": "cashup",
    "member.invite": "people",
    "member.role": "people",
    "member.remove": "people",
    "staff.assign": "people",
    "staff.unassign": "people",
    "org.limits": "settings",
    "org.features": "settings",
    "guest.delete": "people",
    "org.mfa": "settings",
    "org.tablets": "settings",
    "training.create": "settings",
    "training.save": "settings",
    "training.delete": "settings",
};
export function isAuditKind(value) {
    return typeof value === "string" && AUDIT_KINDS.includes(value);
}
/**
 * The most changes one entry keeps. A count of a big back bar can move a
 * couple of hundred levels; past this the entry says how many more there
 * were, and the count's own lines still hold every figure.
 */
export const MAX_AUDIT_CHANGES = 250;
/** Long enough for a note or a reason; short enough that an entry stays small. */
export const MAX_AUDIT_TEXT = 200;
function clip(text) {
    return text.length > MAX_AUDIT_TEXT ? `${text.slice(0, MAX_AUDIT_TEXT)}…` : text;
}
/**
 * A value as the log keeps it: scalars as they are, strings clipped, lists
 * and objects as clipped JSON. `undefined` means "absent" and stays absent,
 * so a field that did not exist before reads as nothing rather than null.
 */
export function auditValue(value) {
    if (value === undefined)
        return undefined;
    if (value === null)
        return null;
    if (typeof value === "number")
        return Number.isFinite(value) ? value : null;
    if (typeof value === "boolean")
        return value;
    if (typeof value === "string")
        return clip(value);
    try {
        return clip(stableJson(value));
    }
    catch {
        return null;
    }
}
/**
 * JSON with object keys in order, so `{bartender, manager}` and `{manager,
 * bartender}` are the same limits rather than a change nobody made. Lists
 * keep their order: a build's lines in a new order is a different build.
 */
function stableJson(value) {
    return JSON.stringify(value, (_key, v) => v && typeof v === "object" && !Array.isArray(v)
        ? Object.fromEntries(Object.entries(v).sort(([a], [b]) => a.localeCompare(b)))
        : v);
}
/**
 * Absent and null are the same thing to a person reading the log: "no
 * location" either way. Everything else compares by its JSON, so a list
 * re-saved in the same order is not a change.
 */
function same(a, b) {
    const norm = (v) => (v === undefined || v === null ? null : v);
    return stableJson(norm(a)) === stableJson(norm(b));
}
/**
 * The fields of `fields` that differ between two versions of a row.
 *
 * `valuesKeptElsewhere` names fields whose change is recorded without their
 * values — a spec's build, which the version snapshot already holds.
 * Fields are reported in the order given, so an entry reads the same way
 * every time.
 */
export function diffFields(before, after, fields, options = {}) {
    // `object` so a typed row — a StockLevel, a Spec — can be passed as it is;
    // the fields are read by name either way.
    const from = (before ?? {});
    const to = (after ?? {});
    const out = [];
    for (const field of fields) {
        const was = from[field];
        const now = to[field];
        if (same(was, now))
            continue;
        const item = options.item !== undefined ? { item: options.item } : {};
        if (options.valuesKeptElsewhere?.includes(field)) {
            out.push({ field, ...item });
            continue;
        }
        const b = auditValue(was);
        const a = auditValue(now);
        out.push({
            field,
            ...item,
            ...(b !== undefined ? { before: b } : {}),
            ...(a !== undefined ? { after: a } : {}),
        });
    }
    return out;
}
/**
 * The entry as it is stored: the draft plus who, when and an id, with its
 * changes capped and its detail reduced to values the log can keep.
 */
export function shapeAuditEntry(draft, actor, at, auditId) {
    const changes = draft.changes ?? [];
    const detail = {};
    for (const [key, value] of Object.entries(draft.detail ?? {})) {
        const kept = auditValue(value);
        if (kept !== undefined)
            detail[key] = kept;
    }
    const extra = changes.length - MAX_AUDIT_CHANGES;
    return {
        auditId,
        at,
        event: draft.event,
        kind: AUDIT_EVENTS[draft.event],
        action: draft.action,
        actorId: actor.userId,
        actorName: actor.name,
        venueId: draft.venueId,
        target: {
            type: draft.target.type,
            id: draft.target.id,
            ...(draft.target.label ? { label: clip(draft.target.label) } : {}),
        },
        changes: extra > 0 ? changes.slice(0, MAX_AUDIT_CHANGES) : [...changes],
        detail,
        ...(extra > 0 ? { truncated: extra } : {}),
    };
}
/**
 * What to call the actor: their name in the organisation, else their email,
 * else — for a Venox operator acting in somebody else's organisation, who
 * has no member row there — "Venox". The same rule the chat uses to name
 * who asked, so a person is called one thing across the product.
 */
export function actorNameFrom(row, platform) {
    // A blank name falls through to the email, not to "Someone": a member
    // invited without a display name still has an address worth showing.
    for (const field of ["displayName", "email"]) {
        const value = row?.[field];
        if (typeof value === "string" && value.trim())
            return value.trim();
    }
    return platform ? "Venox" : "Someone";
}
// ------------------------------------------------------------------ paging
/** The most entries one page of the Activity view returns. */
export const MAX_AUDIT_PAGE = 100;
export const DEFAULT_AUDIT_PAGE = 50;
/**
 * A page cursor is the `<at>#<auditId>` of the last entry on the page before.
 * Plain rather than an encoded DynamoDB key: the partition is always the
 * caller's own, so the cursor carries no tenant and there is nothing in it
 * worth hiding or able to point anywhere else.
 */
const CURSOR = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z#[A-Za-z0-9_-]{1,40}$/;
export function isAuditCursor(value) {
    return typeof value === "string" && CURSOR.test(value);
}
/** A page size from a query string: the default when absent, clamped when silly. */
export function auditPageSize(raw) {
    const n = raw === undefined ? NaN : Number.parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1)
        return DEFAULT_AUDIT_PAGE;
    return Math.min(n, MAX_AUDIT_PAGE);
}
// ---------------------------------------------------------------- reading
const HEADLINES = {
    "stock.adjust": "Set a stock level",
    "count.finalise": "Finalised a stocktake",
    "batch.make": "Made a batch",
    "price.override": "Changed a venue price",
    "ingredient.cost": "Changed an ingredient's cost",
    "spec.create": "Created a recipe",
    "spec.save": "Saved a recipe",
    "item.save": "Saved an item",
    "recipe.promote": "Made a recipe the House recipe",
    "order.approve": "Approved an order",
    "order.reject": "Rejected an order",
    "order.cancel": "Cancelled an order",
    "wastage.approve": "Approved wastage",
    "wastage.delete": "Deleted wastage",
    "cashup.finalise": "Finalised a cash-up",
    "member.invite": "Invited a member",
    "member.role": "Changed an org role",
    "member.remove": "Removed a member",
    "staff.assign": "Assigned to a venue",
    "staff.unassign": "Took off a venue",
    "org.limits": "Changed approval limits",
    "org.features": "Venox changed which features are on",
    "guest.delete": "Deleted a guest's record",
    "org.mfa": "Changed who must use two-step sign-in",
    "org.tablets": "Changed how tablets lock and what needs a password",
    "training.create": "Wrote a training module",
    "training.save": "Changed a training module",
    "training.delete": "Deleted a training module",
};
/** What happened, in a few words, for the Activity list. */
export function auditHeadline(entry) {
    if (entry.event === "order.approve" && entry.detail["withinOwnLimit"] === true) {
        return "Sent an order within their own limit";
    }
    return HEADLINES[entry.event] ?? entry.event;
}
/** One change as a line a person reads: `heldMl (gin): 2800 → 700`. */
export function describeChange(change) {
    const label = change.item ? `${change.field} (${change.item})` : change.field;
    if (!("before" in change) && !("after" in change))
        return `${label} changed`;
    const show = (v) => (v === undefined || v === null || v === "" ? "—" : String(v));
    return `${label}: ${show(change.before)} → ${show(change.after)}`;
}
//# sourceMappingURL=audit.js.map